// Walk-in registration — RON-5469 R3. Prototype region: `#bd-walkin`.
//
// ONE CALL registers the person and checks them in (§5). There is deliberately no second step:
//
//   "A walk-in is standing at the desk — creating them `expected` and making the receptionist
//    press a second button would misreport who is in the building for as long as it took."
//
// So the submit button says what actually happens, and the dialog closes onto a board where the
// person is already on site.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { GetReceptionHosts_api, isSuccessfulResponse } from "../ReceptionAPI.jsx";

// Long enough to swallow typing a surname, short enough not to feel laggy at a desk.
const HOST_SEARCH_DEBOUNCE_MS = 250;

const EMPTY = {
  name: "",
  company: "",
  email: "",
  purpose: "",
  expectedOut: "",
  isVip: false,
  requireHs: true,
  requireNda: false,
};

const ReceptionWalkInModal = ({ isOpen, isBusy, onSubmit, onClose, siteName, siteTimezone }) => {
  const { t } = useTranslation("common");
  const [form, setForm] = useState(EMPTY);
  const [host, setHost] = useState(null);
  const [hostQuery, setHostQuery] = useState("");
  const [hostResults, setHostResults] = useState([]);
  const [isHostOpen, setIsHostOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const hostWrapRef = useRef(null);

  // Click away or press Escape to close the host list, like every other menu on this screen.
  // Scoped to the wrapper so clicking INSIDE it — the search box, a name — does not close it.
  useEffect(() => {
    if (!isHostOpen) return undefined;

    const onDocumentMouseDown = (event) => {
      if (hostWrapRef.current?.contains(event.target)) return;

      setIsHostOpen(false);
    };
    // Stopped from bubbling: the dialog itself closes on Escape, and one key press should shut
    // the list without also shutting the form the receptionist is halfway through.
    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;

      event.stopPropagation();
      setIsHostOpen(false);
    };

    document.addEventListener("mousedown", onDocumentMouseDown);
    document.addEventListener("keydown", onKeyDown, true);

    return () => {
      document.removeEventListener("mousedown", onDocumentMouseDown);
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [isHostOpen]);

  const setField = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  // A fresh dialog each time it opens. Without this, the last walk-in's details are still in the
  // boxes for the next person — and at a desk they would get submitted.
  useEffect(() => {
    if (!isOpen) return;

    setForm(EMPTY);
    setHost(null);
    setHostQuery("");
    setHostResults([]);
    setErrors({});
  }, [isOpen]);

  // ── Host type-ahead (§5) ───────────────────────────────────────────────────────────────────
  //
  // `GET /visitor/reception/hosts`, NOT `/visitor/hosts/candidates` — that one is gated on
  // `vcm.host.view`, which is admin-only, so a receptionist gets a 403.
  //
  // It lists every ACTIVE EMPLOYEE, not only designated hosts: "a visitor asks for a person by
  // name and that person need not hold the visitor capability". `is_host` only badges them.
  const searchToken = useRef(0);

  const searchHosts = useCallback(async (term) => {
    const token = ++searchToken.current;

    try {
      const response = await GetReceptionHosts_api({ Query: term });

      // A later keystroke already owns the list.
      if (token !== searchToken.current || !isSuccessfulResponse(response)) return;

      setHostResults(response.Data.Records.items || []);
    } catch (error) {
      console.error("GetReceptionHosts error", error);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;

    const timer = setTimeout(() => searchHosts(hostQuery.trim()), HOST_SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [hostQuery, isOpen, searchHosts]);

  // Designated hosts first, then everyone else — both are selectable, because the API
  // deliberately does not filter non-hosts out and neither should the desk.
  const sortedHosts = useMemo(
    () =>
      [...hostResults].sort((a, b) => Number(b.is_host) - Number(a.is_host)),
    [hostResults]
  );

  if (!isOpen) return null;

  const validate = () => {
    const next = {};

    // Both are 422s server-side; caught here so the desk sees which field, not a status code.
    if (!form.name.trim()) next.name = t("react_reception_error_name");
    if (!host) next.host = t("react_reception_error_host");
    // Optional, but validated when present — a typo means the host is told about somebody the
    // system can never contact again.
    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      next.email = t("react_reception_error_email");
    }

    setErrors(next);

    return Object.keys(next).length === 0;
  };

  const submit = () => {
    if (!validate()) return;

    onSubmit?.({
      Name: form.name,
      HostID: host.id,
      Company: form.company,
      Email: form.email,
      Purpose: form.purpose,
      ExpectedOut: form.expectedOut,
      IsVip: form.isVip,
      RequireHs: form.requireHs,
      RequireNda: form.requireNda,
    });
  };

  return (
    <div className="reception-modal__backdrop" role="presentation" onClick={onClose}>
      <div
        className="reception-modal reception-modal--wide"
        role="dialog"
        aria-modal="true"
        aria-label={t("react_reception_walk_in_title")}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="reception-modal__head">
          {/* The prototype's orange icon chip — the same orange as the Walk-in button that opened
              this dialog, so the two read as one action rather than two unrelated oranges. */}
          <span className="reception-modal__ico reception-modal__ico--orange" aria-hidden="true">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              {/* A plain plus, like the prototype — and like the Walk-in button that opened this.
                  A person-plus glyph said "add a person" twice over in a dialog already titled
                  "Register a walk-in visitor". */}
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </span>

          {/* Title and subtitle TOGETHER inside the head, as the prototype has them. Ours put the
              subtitle after the head, so it fell below the dividing rule and read as body copy. */}
          <span className="reception-modal__titles">
            <h2>{t("react_reception_walk_in_title")}</h2>
            <span className="reception-modal__sub">{t("react_reception_walk_in_sub")}</span>
          </span>

          <button
            type="button"
            className="reception-modal__x"
            onClick={onClose}
            aria-label={t("react_reception_close")}
          >
            ✕
          </button>
        </div>

        <div className="reception-modal__body">
          <div className="reception-field__row">
            <label className="reception-field">
              <span className="reception-field__label">
                {t("name")} <span className="reception-field__req">*</span>
              </span>
              <input
                type="text"
                className="reception-field__input"
                value={form.name}
                placeholder={t("react_reception_ph_name")}
                onChange={(event) => setField("name", event.target.value)}
              />
              {errors.name && <span className="reception-field__error">{errors.name}</span>}
            </label>

            <label className="reception-field">
              <span className="reception-field__label">{t("company")}</span>
              <input
                type="text"
                className="reception-field__input"
                value={form.company}
                placeholder={t("react_reception_ph_company")}
                onChange={(event) => setField("company", event.target.value)}
              />
            </label>
          </div>

          {/* The host is REQUIRED. "Somebody at a desk is there to see a person, and a visit with
              no host is one nobody can be told about or escort." */}
          <div className="reception-field" ref={hostWrapRef}>
            <span className="reception-field__label">
              {t("react_reception_col_host")} <span className="reception-field__req">*</span>
            </span>

            {/* A SELECT THAT CAN BE SEARCHED, like the prototype's `<select>` — not a bare search
                box with a list permanently underneath it, which is what this was.

                The difference matters: a closed control shows the current answer and takes one
                click to change, where an open list pushed the rest of the form down the dialog and
                never told you whether anybody was chosen. It is also the same shape as the site
                switcher at the top of this screen, so there is one picker pattern here, not two.

                The search is still server-side (`GET /reception/hosts?q=`, §5), because the list is
                every active employee and capped at 50 — far too many to ship into the browser. */}
            <button
              type="button"
              className="reception-hostpick__trigger"
              onClick={() => setIsHostOpen((open) => !open)}
              aria-haspopup="listbox"
              aria-expanded={isHostOpen}
            >
              <span
                className={`reception-hostpick__value${
                  host ? "" : " reception-hostpick__value--empty"
                }`}
              >
                {host ? host.name : t("react_reception_host_select_placeholder")}
              </span>
              {host?.is_host && (
                <span className="reception-tag">{t("react_reception_designated_host")}</span>
              )}
              <svg
                className="reception-hostpick__caret"
                width="11"
                height="11"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>

            {isHostOpen && (
              <div className="reception-hostpick__menu" role="listbox">
                <input
                  type="text"
                  className="reception-field__input reception-hostpick__search"
                  value={hostQuery}
                  onChange={(event) => setHostQuery(event.target.value)}
                  placeholder={t("react_reception_host_placeholder")}
                  aria-label={t("react_reception_col_host")}
                  autoFocus
                />

                <ul className="reception-hostlist">
                  {sortedHosts.map((candidate) => (
                    <li key={candidate.id}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={host?.id === candidate.id}
                        className="reception-hostlist__item"
                        onClick={() => {
                          setHost(candidate);
                          setIsHostOpen(false);
                          setErrors((current) => ({ ...current, host: undefined }));
                        }}
                      >
                        <span>{candidate.name}</span>
                        <span className="reception-hostlist__email">{candidate.email}</span>
                        {/* Marked, not filtered — a visitor can ask for anyone. */}
                        {candidate.is_host && (
                          <span className="reception-tag">
                            {t("react_reception_designated_host")}
                          </span>
                        )}
                      </button>
                    </li>
                  ))}
                  {sortedHosts.length === 0 && (
                    <li className="reception-hostlist__empty">
                      {t("react_reception_no_hosts")}
                    </li>
                  )}
                </ul>
              </div>
            )}

            {errors.host && <span className="reception-field__error">{errors.host}</span>}
          </div>

          <div className="reception-field__row">
            <label className="reception-field">
              <span className="reception-field__label">{t("email")}</span>
              <input
                type="email"
                className="reception-field__input"
                value={form.email}
                placeholder={t("react_reception_ph_email")}
                onChange={(event) => setField("email", event.target.value)}
              />
              {errors.email ? (
                <span className="reception-field__error">{errors.email}</span>
              ) : (
                <span className="reception-field__help">
                  {t("react_reception_email_help")}
                </span>
              )}
            </label>

            <label className="reception-field">
              <span className="reception-field__label">
                {t("react_reception_expected_out")}
              </span>
              <input
                type="time"
                className="reception-field__input"
                value={form.expectedOut}
                onChange={(event) => setField("expectedOut", event.target.value)}
              />
              {/* WHICH CLOCK. The time is sent as a bare wall-clock reading against the site's
                  own day, and the visitor is standing at that site — so it means the site's local
                  time, not the browser's.

                  That matters the moment a desk covers more than one building: somebody in Dublin
                  registering a walk-in at the London site types 17:00 meaning London, and nothing
                  on the form said which it was. Naming the timezone is the honest fix; silently
                  converting would be worse, because the server may already treat it as site-local
                  and a second shift would put every expected-out an hour wrong. Raised with
                  Laravel — see docs/RON-5469_open-questions.md. */}
              {siteTimezone && (
                <span className="reception-field__help">
                  {t("react_reception_time_in_zone")
                    .replace("%s", siteName || "")
                    .replace("%z", siteTimezone)}
                </span>
              )}
            </label>
          </div>

          <label className="reception-field">
            <span className="reception-field__label">{t("react_reception_purpose")}</span>
            <textarea
              className="reception-field__input reception-field__input--area"
              rows={2}
              value={form.purpose}
              placeholder={t("react_reception_ph_purpose")}
              onChange={(event) => setField("purpose", event.target.value)}
            />
          </label>

          {/* The prototype's `.toggle-row`s: a bordered row each, with the switch on the right and
              a line of explanation under the label. Ours were bare checkboxes with a label, so a
              receptionist had to know what "Require H&S form" meant before pressing it.

              In the prototype's ORDER too — H&S, NDA, then VIP. The first two are compliance and
              belong together; VIP is a different kind of fact and goes last. */}
          <div className="reception-switches">
            <label className="reception-switch">
              <span className="reception-switch__text">
                <span className="reception-switch__title">{t("react_reception_require_hs")}</span>
                <span className="reception-switch__sub">{t("react_reception_require_hs_sub")}</span>
              </span>
              <input
                type="checkbox"
                checked={form.requireHs}
                onChange={(event) => setField("requireHs", event.target.checked)}
              />
              <span className="reception-switch__track" aria-hidden="true" />
            </label>

            <label className="reception-switch">
              <span className="reception-switch__text">
                <span className="reception-switch__title">{t("react_reception_require_nda")}</span>
                <span className="reception-switch__sub">{t("react_reception_require_nda_sub")}</span>
              </span>
              <input
                type="checkbox"
                checked={form.requireNda}
                onChange={(event) => setField("requireNda", event.target.checked)}
              />
              <span className="reception-switch__track" aria-hidden="true" />
            </label>

            <label className="reception-switch">
              <span className="reception-switch__text">
                <span className="reception-switch__title">{t("react_reception_mark_vip")}</span>
                <span className="reception-switch__sub">{t("react_reception_mark_vip_sub")}</span>
              </span>
              <input
                type="checkbox"
                checked={form.isVip}
                onChange={(event) => setField("isVip", event.target.checked)}
              />
              <span className="reception-switch__track" aria-hidden="true" />
            </label>
          </div>

          {/* Said out loud, because the switches look like compliance and are not. A16: they are
              stored, nothing reads them yet, and they must never render as a tick in the
              compliance panel — a `1` here means "the desk said this visit needs one", not that
              anybody was asked. */}
          <p className="reception-modal__note">{t("react_reception_require_note")}</p>
        </div>

        <div className="reception-modal__foot">
          <button type="button" className="reception-btn reception-btn--ghost" onClick={onClose}>
            {t("cancel")}
          </button>
          {/* Names what actually happens: one press registers them AND checks them in. */}
          <button type="button" className="reception-btn" disabled={isBusy} onClick={submit}>
            {t("react_reception_register_and_check_in")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReceptionWalkInModal;
